import type { QueryClient } from "@tanstack/react-query";
import { useRouter } from "@tanstack/react-router";
import { useEffect } from "react";
import { subscribeToSession } from "@/lib/session-request-scope";
import { transitionSession } from "@/lib/session-transition";

const CHANNEL_NAME = "slotique-session";
const INVALIDATION_TYPE = "session-invalidated";

export function useBrowserSession(queryClient: QueryClient): void {
  const router = useRouter();

  useEffect(() => {
    const channel = new BroadcastChannel(CHANNEL_NAME);
    let receiving = false;
    const invalidate = () => {
      receiving = true;
      try {
        transitionSession(queryClient);
      } finally {
        receiving = false;
      }
      // The cleared me query forces guards to verify the shared cookie again.
      void router.invalidate();
    };
    const unsubscribe = subscribeToSession((owner) => {
      if (owner === queryClient && !receiving) {
        channel.postMessage({ type: INVALIDATION_TYPE });
        if (queryClient.getQueryData(["me"]) === null) void router.invalidate();
      }
    });
    const onMessage = (event: MessageEvent<unknown>) => {
      if (
        typeof event.data === "object" &&
        event.data !== null &&
        "type" in event.data &&
        event.data.type === INVALIDATION_TYPE
      ) {
        invalidate();
      }
    };
    const onPageShow = (event: PageTransitionEvent) => {
      if (event.persisted) invalidate();
    };
    channel.addEventListener("message", onMessage);
    window.addEventListener("pageshow", onPageShow);
    return () => {
      unsubscribe();
      channel.removeEventListener("message", onMessage);
      channel.close();
      window.removeEventListener("pageshow", onPageShow);
    };
  }, [queryClient, router]);
}
